import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { AiSpend } from '../ai/AiSpend';
import { attachmentBlocks, attachmentLabel, type Attachment } from '../ai/attachments';
import { AttachButton, AttachChips } from '../ai/AttachmentControls';
import { Button, Card, Input } from '../ui';
import { ProposalConfirm, type Proposal } from '../ai/ProposalConfirm';
import { traceChips, type TraceEntry } from './aiTrace';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** AI-2: the deterministic reads that grounded an assistant answer, rendered as chips below it. */
  trace?: TraceEntry[];
}

/** Farsi starter prompts — click to fill the box; grounded in the live control-tower data. */
const STARTERS = [
  'گزارش کوتاه وضعیت امروز را بده',
  'کدام پیشنهادها زیر حد حاشیه‌اند و چرا؟',
  'اگر مس ۱۵٪ گران شود، سود مالک چقدر تغییر می‌کند؟',
  'کدام چک‌ها به‌زودی سررسید می‌شوند؟',
  'پوشش بهای واقعی را چطور بالا ببرم؟',
  'کدام مشتری بیشترین سود ناخالص را دارد؟',
];

/** Browser speech-to-text (Web Speech API, fa-IR). Chrome/Edge/Safari; null when unsupported. */
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}
function makeRecognizer(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = 'fa-IR';
  rec.interimResults = false;
  rec.continuous = false;
  return rec;
}

export function AiChatScreen({
  onOpenDeal,
  seed,
  onSeedConsumed,
}: {
  onOpenDeal?: ((id: number) => void) | undefined;
  /** AI-3: a deep-link question (e.g. from a deal screen) to pre-fill the composer — consumed on mount. */
  seed?: string | undefined;
  onSeedConsumed?: (() => void) | undefined;
}) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // AI-1: when the analyst proposes a data-entry action, hold it here for the shared confirm card.
  const [pending, setPending] = useState<Proposal | null>(null);
  const [listening, setListening] = useState(false);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [spendTick, setSpendTick] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const voiceSupported = useRef<boolean>(
    typeof window !== 'undefined' &&
      !!(
        (window as unknown as { SpeechRecognition?: unknown }).SpeechRecognition ??
        (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition
      ),
  );

  function toggleVoice() {
    if (listening) {
      recRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = makeRecognizer();
    if (!rec) return;
    recRef.current = rec;
    rec.onresult = (e) => {
      const said = Array.from({ length: e.results.length }, (_, i) => e.results[i]![0]!.transcript)
        .join(' ')
        .trim();
      if (said) setInput((prev) => (prev ? `${prev} ${said}` : said));
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    setListening(true);
    rec.start();
  }

  useEffect(() => {
    void api<{ configured: boolean }>('/api/ai/config').then((r) => {
      if (r.ok) setConfigured((r.body as { configured: boolean }).configured);
    });
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);
  useEffect(() => () => recRef.current?.stop(), []);
  // AI-3: a deep-link seed FILLS the composer (same setter the STARTERS chips use) — never auto-sends, so the
  // human still reviews before any model spend — then is consumed so a later manual AI-tab visit is clean.
  useEffect(() => {
    if (seed) {
      setInput(seed);
      onSeedConsumed?.();
    }
  }, [seed, onSeedConsumed]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const q = input.trim();
    if ((!q && files.length === 0) || busy) return;
    setError('');
    setPending(null); // a new question supersedes any un-acted proposal

    const attached = files;
    const displayText = [q, attachmentLabel(attached)].filter(Boolean).join('  ');

    // History goes as text (attachments ride only the turn they're added, to keep requests cheap);
    // the composed turn carries the real image/PDF blocks.
    const apiHistory = messages.map((m) => ({ role: m.role, content: m.content }));
    const apiUser = attached.length
      ? {
          role: 'user' as const,
          content: [
            ...attachmentBlocks(attached),
            { type: 'text', text: q || 'این فایل را بررسی و تحلیل کن.' },
          ],
        }
      : { role: 'user' as const, content: q };

    const nextUi: ChatMessage[] = [...messages, { role: 'user', content: displayText }];
    setMessages(nextUi);
    setInput('');
    setFiles([]);
    setBusy(true);
    const r = await api<{ reply: string; trace?: TraceEntry[]; proposal?: Proposal }>(
      '/api/ai/chat',
      {
        method: 'POST',
        body: JSON.stringify({ messages: [...apiHistory, apiUser] }),
      },
    );
    setBusy(false);
    if (!r.ok) {
      setError((r.body as { message?: string }).message ?? 'خطا در پاسخ هوش مصنوعی');
      setMessages(messages); // roll back the optimistic user turn
      setInput(q);
      setFiles(attached); // restore the attachments so nothing is lost
      return;
    }
    const body = r.body as { reply: string; trace?: TraceEntry[]; proposal?: Proposal };
    setMessages([
      ...nextUi,
      { role: 'assistant', content: body.reply, ...(body.trace ? { trace: body.trace } : {}) },
    ]);
    if (body.proposal) setPending(body.proposal); // AI-1: surface the confirm card below the reply
    setSpendTick((t) => t + 1);
  }

  return (
    <Card title="دستیار تحلیل (Claude)">
      <p className="muted" style={{ marginTop: 0 }}>
        دادهٔ زندهٔ برج کنترل (پیشنهادها، حاشیه‌ها، هشدارها) به‌صورت خودکار در اختیار دستیار است —
        بپرسید: «چرا حاشیه این هفته افت کرد؟»، «مس ۱۵٪ گران شود چه می‌شود؟» — یا با 📎 یک تصویر یا
        PDF (مثلاً پیش‌فاکتور رقیب یا لیست قیمت) پیوست کنید تا تحلیل شود.
      </p>

      <AiSpend refresh={spendTick} />

      {configured === false ? (
        <div className="banner">
          کلید API تنظیم نشده — ابتدا در «تنظیمات» بخش هوش مصنوعی، کلید Anthropic را وارد کنید.
        </div>
      ) : null}

      <div className="chatlog">
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <p className="muted" style={{ marginTop: 0 }}>
              یک سؤال بپرسید یا از این نمونه‌ها شروع کنید:
            </p>
            <div
              className="row"
              style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}
            >
              {STARTERS.map((s) => (
                <button key={s} type="button" className="starter-chip" onClick={() => setInput(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => {
            const chips = m.trace ? traceChips(m.trace) : [];
            return (
              <div
                key={i}
                className={m.role === 'user' ? 'bubble bubble-user' : 'bubble bubble-ai'}
              >
                {m.content}
                {chips.length > 0 ? (
                  <div className="ai-trace" aria-label="مبنای این پاسخ">
                    {chips.map((c) =>
                      c.dealId != null && onOpenDeal ? (
                        <button
                          key={c.key}
                          type="button"
                          className="chip chip-info ai-trace-link"
                          onClick={() => onOpenDeal(c.dealId!)}
                          title="باز کردن این قرارداد"
                        >
                          {c.label}
                        </button>
                      ) : (
                        <span key={c.key} className="chip chip-neutral">
                          {c.label}
                        </span>
                      ),
                    )}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
        {busy ? <div className="bubble bubble-ai muted">در حال تحلیل…</div> : null}
        <div ref={endRef} />
      </div>

      {pending ? (
        <ProposalConfirm
          proposal={pending}
          onConfirmed={() => {
            setMessages((m) => [...m, { role: 'assistant', content: '✓ با موفقیت ثبت شد.' }]);
            setPending(null);
          }}
          onCancel={() => {
            setMessages((m) => [...m, { role: 'assistant', content: 'لغو شد — چیزی ثبت نشد.' }]);
            setPending(null);
          }}
        />
      ) : null}

      {error ? <div className="field-error">{error}</div> : null}
      <AttachChips files={files} setFiles={setFiles} />
      <form onSubmit={send} className="row" style={{ marginTop: 10 }}>
        <div style={{ flex: 1 }}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="سؤال خود را بنویسید…"
            disabled={busy || configured === false}
            autoFocus
          />
        </div>
        <AttachButton
          files={files}
          setFiles={setFiles}
          onError={setError}
          disabled={busy || configured === false}
        />
        {voiceSupported.current ? (
          <Button
            type="button"
            variant={listening ? 'primary' : 'secondary'}
            onClick={toggleVoice}
            disabled={busy || configured === false}
            aria-label="گفتن سؤال با میکروفن"
          >
            {listening ? '⏹ در حال شنیدن…' : '🎤 صحبت کنید'}
          </Button>
        ) : null}
        <Button type="submit" loading={busy} disabled={configured === false}>
          ارسال
        </Button>
      </form>
      {voiceSupported.current ? (
        <p className="muted" style={{ marginBottom: 0, fontSize: 11 }}>
          🎤 گفتار فارسی به متن تبدیل می‌شود (داخل مرورگر) — بگویید، ویرایش کنید، ارسال بزنید.
        </p>
      ) : null}
    </Card>
  );
}
